import type { FormField } from '../../interfaces/form.interface';
import type { InputType } from '../../interfaces/input.interface';
import {
    ReactiveRepeatableFieldFactory,
    type ReactiveRepeatableFieldColumn,
} from '../repeatable/reactive-repeatable-field-factory';
import { ReactiveModalReadonlyFieldFactory } from './reactive-modal-readonly-field-factory';

const DEFAULT_MODAL_MATERIAL_LINE_COUNT = 4;
const DEFAULT_MODAL_MATERIAL_FIELD_WIDTH = 3;

export interface ReactiveModalMaterialLineColumnConfig {
    readonly sourcePrefix: string;
    readonly label: string;
    readonly width?: number;
    readonly type?: InputType;
}

export interface ReactiveModalMaterialLineFactoryConfig {
    readonly lineCount?: number;
    readonly columns?: readonly ReactiveModalMaterialLineColumnConfig[];
    readonly startOrder?: number;
    readonly width?: number;
}

/** Builds the standard read-only material line block used by list modals. */
export class ReactiveModalMaterialLineFactory {
    public static createFields<TModel>(config: ReactiveModalMaterialLineFactoryConfig = {}): FormField<TModel>[] {
        const width = config.width ?? DEFAULT_MODAL_MATERIAL_FIELD_WIDTH;

        return ReactiveRepeatableFieldFactory.createFields<TModel>({
            lineCount: config.lineCount ?? DEFAULT_MODAL_MATERIAL_LINE_COUNT,
            ...(config.startOrder !== undefined ? { startOrder: config.startOrder } : {}),
            columns: this.createColumns<TModel>(config.columns ?? this.getDefaultColumns(), width),
        });
    }

    private static createColumns<TModel>(
        columns: readonly ReactiveModalMaterialLineColumnConfig[],
        width: number,
    ): readonly ReactiveRepeatableFieldColumn<TModel>[] {
        return columns.map((column) => ({
            sourcePrefix: column.sourcePrefix,
            label: ({ lineIndex }) => ReactiveRepeatableFieldFactory.indexedLabel(column.label, lineIndex),
            createField: (context) => ReactiveModalReadonlyFieldFactory.create<TModel>({
                name: context.name,
                label: context.label,
                order: context.order,
                width: column.width ?? width,
                ...(column.type !== undefined ? { type: column.type } : {}),
            }),
        }));
    }

    private static getDefaultColumns(): readonly ReactiveModalMaterialLineColumnConfig[] {
        return [
            { sourcePrefix: 'cantidad', label: 'Cantidad' },
            { sourcePrefix: 'maquinaria_nombre_', label: 'Concepto' },
            { sourcePrefix: 'precio', label: 'Precio' },
            { sourcePrefix: 'total', label: 'Total' },
        ];
    }
}
